import dayjs from "dayjs";
import $ from "jquery";
import { resourcePath } from "./paths";
import "flatpickr/dist/flatpickr.min.css";
import Decimal from "decimal.js";
import flatpickr from "flatpickr";
import { Japanese } from "flatpickr/dist/l10n/ja.js";
import { Selectbox } from "./Form";
import { GSL_ANALYSIS_START } from "./GslTime";
import type { MapModel } from "./MapMeta";
import { MSG_ANALYSIS_START } from "./MsgTime";

export type ShadeVariable =
    | "GSMR" | "GSMRG" | "GSMC" | "GSMRC" | "GSMSj" | "GSMSh" | "GSMSv" | "GSMV" | "GSMN"
    | "GS5R" | "GS5Sj" | "GS5Sh" | "GS5N"
    | "GSAR" | "GSASj" | "GSASh" | "GSASv" | "GSAN"
    | "GSLR" | "GSLN"
    | "MSGL"
    | "MSMR" | "MSMRG" | "MSMRC" | "MSMT" | "MSMSj" | "MSMSh" | "MSMSv" | "MSMV" | "MSMN"
    | "LFMR" | "LFMRC" | "LFMT" | "LFMSj" | "LFMSh" | "LFMSv" | "LFMN"
    | "CWMH"
    | "QMAR" | "QMASj" | "QMASh" | "QMAN"; // biome-ignore format:

export const shadeVariables: ShadeVariable[] = [
    "GSMR", "GSMRG", "GSMC", "GSMRC", "GSMSj", "GSMSh", "GSMSv", "GSMV", "GSMN",
    "GS5R", "GS5Sj", "GS5Sh", "GS5N",
    "GSAR", "GSASj", "GSASh", "GSASv", "GSAN",
    "GSLR", "GSLN",
    "MSGL",
    "MSMR", "MSMRG", "MSMRC", "MSMT", "MSMSj", "MSMSh", "MSMSv", "MSMV", "MSMN",
    "LFMR", "LFMRC", "LFMT", "LFMSj", "LFMSh", "LFMSv", "LFMN",
    "CWMH",
    "QMAR", "QMASj", "QMASh", "QMAN",
]; // biome-ignore format:

export class ShadeSelector {
    private readonly selector = new Selectbox("#EL");
    private changeCallback: () => void = () => {};
    constructor(private readonly params: URLSearchParams) {
        this.selector.onChange(() => this.changeCallback());
        this.loadParams();
    }
    loadParams() {
        if (this.params.has("el_elm")) {
            const requested = this.params.get("el_elm")!;
            // UI から選択肢を外した要素 (GS5 等の旧 URL) は既定へフォールバック
            const optionExists =
                document.querySelector(`#EL option[value="${requested}"]`) !==
                null;
            if (
                shadeVariables.includes(requested as ShadeVariable) &&
                optionExists
            ) {
                this.selector.value = requested;
            } else {
                console.warn(`el_elm=${requested} is invalid.`);
                this.selector.value = "MSMR";
            }
        } else {
            this.selector.value = "MSMR";
        }
    }
    saveParams() {
        this.params.set("el_elm", this.selector.value);
    }
    getParams() {
        return {
            shadeVariable: this.getShadeVariable(),
            model: this.getModel(),
        };
    }
    private getShadeVariable(): ShadeVariable {
        return this.selector.value as ShadeVariable;
    }
    private getModel(): model {
        return this.selector.value.slice(0, 3) as model;
    }
    onChange(callback: () => void) {
        this.changeCallback = callback;
    }
}

export type model = MapModel;

export class InitialTime {
    private static latests = new Map<string, dayjs.Dayjs>();
    private static refreshTask: Promise<void> | null = null;
    static fetch() {
        return this.refresh();
    }
    private static async refresh() {
        if (this.refreshTask) {
            return this.refreshTask;
        }
        const task = (async () => {
            const response = await fetch(
                `${resourcePath("latest.json")}?${Date.now()}`,
            );
            if (!response.ok) {
                throw new Error(`latest.json: HTTP ${response.status}`);
            }
            const latests = await response.json();
            const next = new Map<string, dayjs.Dayjs>();
            for (const [k, v] of Object.entries(latests)) {
                const ini = dayjs(String(v), "YYYY-MM-DD HH:mm:ss");
                if (ini.isValid()) {
                    next.set(k, ini);
                }
            }
            this.applyAliases(next);
            this.latests = next;
        })();
        this.refreshTask = task;
        try {
            await task;
        } finally {
            if (this.refreshTask === task) {
                this.refreshTask = null;
            }
        }
    }
    private static applyAliases(latests: Map<string, dayjs.Dayjs>) {
        for (const [k, ini] of [...latests]) {
            //無地対応
            if (k === "GS5R") {
                latests.set("GS5N", ini);
            }
            if (k === "GSAR") {
                latests.set("GSAN", ini);
            }
            if (k === "GSLR") {
                latests.set("GSLN", ini);
            }
            if (k === "GSMR") {
                latests.set("GSMN", ini);
            }
            if (k === "MSMR") {
                latests.set("MSMN", ini);
            }
            if (k === "LFMR") {
                latests.set("LFMN", ini);
            }
            if (k === "LFMR18") {
                latests.set("LFMN18", ini);
            }

            //雨雲
            if (k === "QMAW") {
                latests.set("QMAN", ini); // 下でQMARはQMAWにしているので
            }
            if (k === "GSMC") {
                latests.set("GSMRC", ini);
            }
            if (k === "MSMC") {
                latests.set("MSMRC", ini);
            }
            if (k === "LFMC") {
                latests.set("LFMRC", ini);
            }
            if (k === "LFMC18") {
                latests.set("LFMRC18", ini);
            }

            //解析(雨)はQMA待ち
            if (k === "QMAW") {
                latests.set("QMAR", ini);
            }
        }
    }
    private picker: flatpickr.Instance = flatpickr(
        "#datetimepicker",
    ) as flatpickr.Instance;
    private changeCallback: () => void = () => {};
    private ini: dayjs.Dayjs = dayjs();
    private previousShadeVariable: ShadeVariable | null = null;
    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
    ) {
        this.init();
    }
    init() {
        this.picker.destroy();
        Japanese.firstDayOfWeek = 0;
        const { shadeVariable, model } = this.shadeSelector.getParams();
        const allowTimesMap = new Map<model, string[]>([
            ["GSM",["03:00","09:00","15:00","21:00"]],
            ["GS5",["03:00","09:00","15:00","21:00"]],
            ["GSA",["03:00","09:00","15:00","21:00"]],
            ["GSL",["03:00","09:00","15:00","21:00"]],
            ["MSG",["21:00"]],
            ["MSM",["00:00","03:00","06:00","09:00","12:00","15:00","18:00","21:00"]],
            ["LFM",["00:00","01:00","02:00","03:00","04:00","05:00","06:00","07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00","23:00"]],
            ["CWM",["03:00","09:00","15:00","21:00"]],
            ["QMA",["00:00","01:00","02:00","03:00","04:00","05:00","06:00","07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00","23:00"]],
        ]); // biome-ignore format:
        // モデル別のデータ保有範囲 (2026-07-23 に draw51 archives を実地調査。
        // 代表要素 -R/-H/MSGW で確認、同一モデルの他要素も同じ保有期間)。
        // min は連続保有の最古初期値。QMA/CWM は2016年からの旧ブロックが実在する
        // ため min はそこへ置き、途中の欠落期間を gaps (日単位、両端を含む) で
        // 選択不可にする。孤立保存 (201910 台風・202407 等の数初期値) は
        // カレンダーからは選べないが、URL の ini 直指定では従来どおり表示できる。
        const availabilityMap = new Map<
            model,
            { min: string; gaps?: { from: string; to: string }[] }
        >([
            ["GSM", { min: "2024-12-04 03:00" }],
            ["GS5", { min: "2024-12-04 03:00" }],
            ["GSA", { min: "2026-07-21 21:00" }],
            ["GSL", { min: "2026-07-20 09:00" }],
            ["MSG", { min: "2024-12-02 21:00" }],
            ["MSM", { min: "2024-12-04 06:00" }],
            ["LFM", { min: "2024-12-03 14:00" }],
            [
                "CWM",
                {
                    min: "2016-07-04 15:00",
                    gaps: [{ from: "2021-01-01", to: "2024-12-02" }],
                },
            ],
            [
                "QMA",
                {
                    min: "2016-01-11 01:00",
                    gaps: [{ from: "2022-01-01", to: "2024-12-05" }],
                },
            ],
        ]);
        const allowTimes = allowTimesMap.get(model)!;
        const hourIncrement = 24 / allowTimes.length;
        const requested = this.params.has("ini")
            ? dayjs(this.params.get("ini")!, "YYYYMMDDHHmmss")
            : null;
        // 新設モデルをlatest公開前にテストできるよう、明示iniが有効なら
        // date pickerの上限にも利用する。latest公開後は通常経路が優先される。
        const latest =
            InitialTime.latests.get(shadeVariable) ??
            (requested?.isValid() ? requested : this.ini);
        const availability = availabilityMap.get(model)!;
        // URLで保有範囲より古いiniを直指定した場合 (孤立保存の閲覧等) は、
        // 上限のrequested扱いと同様にpicker表示が壊れないよう下限も広げる
        let minDate = dayjs(availability.min, "YYYY-MM-DD HH:mm");
        if (requested?.isValid() && requested.isBefore(minDate)) {
            minDate = requested;
        }
        this.picker = flatpickr(
            "#datetimepicker",
            Object.assign({
                dateFormat: "Y/m/d H:i",
                minDate: minDate.toDate(),
                maxDate: latest.toDate(),
                disable: availability.gaps ?? [],
                hourIncrement,
                minuteIncrement: 0,
                enableTime: true,
                time_24hr: true,
                locale: Japanese,
                onClose: () => {
                    this.ini = dayjs(
                        this.picker.input.value,
                        "YYYY/MM/DD HH:mm",
                    );
                    this.changeCallback();
                },
            }),
        ) as flatpickr.Instance;
        if (this.previousShadeVariable === null) {
            this.ini = requested?.isValid() ? requested : latest;
        } else {
            const previousLatest = InitialTime.latests.get(
                this.previousShadeVariable,
            );
            if (previousLatest && this.ini.isSame(previousLatest)) {
                // 最新のを表示していたら、変更後の最新に。
                this.ini = latest;
            } else if (this.ini.isAfter(latest)) {
                // 変更後の最新より新しい場合。
                this.ini = latest;
            } else {
                // 基本的に同じ初期値を表示するが、無い時間にならないように。
                const hour = this.ini.hour();
                if (model === "MSG") {
                    this.ini = this.ini.subtract((hour + 3) % 24, "hour");
                } else if (model === "GSL") {
                    this.ini = this.ini.subtract((hour + 3) % 6, "hour");
                } else if (
                    model === "GSM" ||
                    model === "GS5" ||
                    model === "GSA" ||
                    model === "CWM"
                ) {
                    this.ini = this.ini.subtract((hour + 3) % 6, "hour");
                } else {
                    this.ini = this.ini.subtract(hour % 3, "hour");
                }
            }
            // 切替先モデルの保有範囲外 (最古より前・欠落期間内) なら最新へ移す
            const inGap = (availability.gaps ?? []).some(
                (gap) =>
                    !this.ini.isBefore(dayjs(gap.from, "YYYY-MM-DD")) &&
                    this.ini.isBefore(
                        dayjs(gap.to, "YYYY-MM-DD").add(1, "day"),
                    ),
            );
            if (this.ini.isBefore(minDate) || inGap) {
                this.ini = latest;
            }
        }
        this.picker.setDate(this.ini.toDate());
        this.previousShadeVariable = shadeVariable;
    }
    getParams() {
        return { ini: this.ini };
    }
    getLatest(name: string) {
        return InitialTime.latests.get(name);
    }
    async hasLatest(name: string, ini: dayjs.Dayjs) {
        let latest = this.getLatest(name);
        if (!latest || ini.isAfter(latest)) {
            try {
                await InitialTime.refresh();
            } catch (error) {
                console.warn("latest.jsonの再取得に失敗しました。", error);
            }
            latest = this.getLatest(name);
        }
        if (!latest && this.params.has("ini") && ini.isSame(this.ini)) {
            // latest切替前の新設モデルは、明示URLに限って実ファイルをprobeする。
            return true;
        }
        return Boolean(latest && !ini.isAfter(latest));
    }
    loadParams() {
        const latest = InitialTime.latests.get(
            this.shadeSelector.getParams().shadeVariable,
        );
        const requested = this.params.has("ini")
            ? dayjs(this.params.get("ini")!, "YYYYMMDDHHmmss")
            : (latest ?? this.ini);
        this.ini =
            requested.isValid() && (!latest || !requested.isAfter(latest))
                ? requested
                : (latest ?? this.ini);
        this.picker.setDate(this.ini.toDate());
    }
    enable() {
        this.picker._input.removeAttribute("disabled");
    }
    disable() {
        this.picker._input.setAttribute("disabled", "disabled");
    }
    saveParams() {
        this.params.set("ini", this.ini.format("YYYYMMDDHHmmss"));
    }
    onChange(callback: () => void) {
        this.changeCallback = callback;
    }
}

export class ForecastTime {
    private readonly ftintMap = new Map<ShadeVariable, number>([
        ["GSMR", 1], ["GSMRG", 1], ["GSMC", 1], ["GSMRC", 1], ["GSMN", 1], ["GSMSj", 1], ["GSMSh", 1], ["GSMSv", 1],
        ["GS5R", 3], ["GS5Sj", 3], ["GS5Sh", 3], ["GS5N", 3],
        ["GSAR", 3], ["GSASj", 3], ["GSASh", 3], ["GSASv", 3], ["GSAN", 3],
        ["GSLR", 6], ["GSLN", 6],
        ["MSGL", 3],
        ["MSMR", 1], ["MSMRG", 1], ["MSMRC", 1], ["MSMN", 1], ["MSMT", 1], ["MSMSj", 1], ["MSMSh", 1], ["MSMSv", 1],
        ["LFMR", 0.5], ["LFMRC", 0.5], ["LFMN", 0.5], ["LFMT", 0.5], ["LFMSj", 0.5], ["LFMSh", 0.5],
        ["CWMH", 3],
        ["QMAR", 1], ["QMAN", 1], ["QMASj", 1], ["QMASh", 1],
        ["GSMV", 3],
        ["MSMV", 3],
    ]); // biome-ignore format:
    private readonly ftminMap = new Map<ShadeVariable, number>([
        ["GSMR", 3], ["GSMRG", 6], ["GSMC", 3], ["GSMRC", 3], ["GSMN", 3], ["GSMSj", 3], ["GSMSh", 3], ["GSMSv", 3],
        ["GS5R", 3], ["GS5Sj", 3], ["GS5Sh", 3], ["GS5N", 3],
        ["GSAR", 3], ["GSASj", 3], ["GSASh", 3], ["GSASv", 3], ["GSAN", 3],
        ["GSLR", -264], ["GSLN", -264],
        ["MSGL", -96],
        ["MSMR", 3], ["MSMRG", 3], ["MSMRC", 3], ["MSMN", 3], ["MSMT", 3], ["MSMSj", 3], ["MSMSh", 3], ["MSMSv", 3],
        ["LFMR", 1], ["LFMRC", 1], ["LFMT", 1], ["LFMN", 1], ["LFMSj", 1], ["LFMSh", 1],
        ["CWMH", 3],
        ["QMAR", -72], ["QMAN", -72], ["QMASj", -72], ["QMASh", -72],
        ["GSMV", 6],
        ["MSMV", 3],
    ]); // biome-ignore format:
    private readonly ftmaxMap = new Map<ShadeVariable, number>([
        ["GSMR", 84], ["GSMRG", 84], ["GSMC", 84], ["GSMRC", 84], ["GSMN", 84], ["GSMSj", 84], ["GSMSh", 84], ["GSMSv", 84],
        ["GS5R", 132], ["GS5Sj", 132], ["GS5Sh", 132], ["GS5N", 132],
        ["GSAR", 132], ["GSASj", 132], ["GSASh", 132], ["GSASv", 132], ["GSAN", 132],
        ["GSLR", 264], ["GSLN", 264],
        ["MSGL", 96],
        ["MSMR", 39], ["MSMRG", 39], ["MSMRC", 39], ["MSMN", 39], ["MSMT", 39], ["MSMSj", 39], ["MSMSh", 39], ["MSMSv", 39],
        ["LFMR", 10], ["LFMRC", 10], ["LFMT", 10], ["LFMN", 10], ["LFMSj", 10], ["LFMSh", 10],
        ["CWMH", 72],
        ["QMAR", 0], ["QMAN", 0], ["QMASj", 0], ["QMASh", 0],
        ["GSMV", 84],
        ["MSMV", 39],
    ]); // biome-ignore format:
    // 上段数字の間隔 (時間)。GSLだけ数字は日数表記 (24hごと) とする。
    private readonly labelStepMap = new Map<ShadeVariable, number>([
        ["GSMR", 6], ["GSMRG", 6], ["GSMC", 6], ["GSMRC", 6], ["GSMN", 6], ["GSMSj", 6], ["GSMSh", 6], ["GSMSv", 6],
        ["GS5R", 6], ["GS5Sj", 6], ["GS5Sh", 6], ["GS5N", 6],
        ["GSAR", 6], ["GSASj", 6], ["GSASh", 6], ["GSAN", 6],
        ["GSLR", 24], ["GSLN", 24],
        ["MSGL", 12],
        ["MSMR", 3], ["MSMRG", 3], ["MSMRC", 3], ["MSMN", 3], ["MSMT", 3], ["MSMSj", 3], ["MSMSh", 3], ["MSMSv", 3],
        ["LFMR", 1], ["LFMRC", 1], ["LFMN", 1], ["LFMT", 1], ["LFMSj", 1], ["LFMSh", 1],
        ["CWMH", 6],
        ["QMAR", 6], ["QMAN", 6], ["QMASj", 6], ["QMASh", 6],
        ["GSMV", 6],
        ["MSMV", 6],
    ]); // biome-ignore format:
    private changeCallback: () => void = () => {};
    private ftint = new Decimal(1);
    private ftmin = new Decimal(3);
    private ftmax = new Decimal(84);
    private ft = new Decimal(3);
    private initFlag = false;
    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
        private readonly initialTime: InitialTime,
    ) {
        $("#hover_onoff").prop("checked", false);
        this.init();
    }
    init() {
        const { shadeVariable } = this.shadeSelector.getParams();
        this.ftint = new Decimal(this.ftintMap.get(shadeVariable)!);
        this.ftmin = new Decimal(this.getMinimum(shadeVariable));
        this.ftmax = new Decimal(this.getMaximum(shadeVariable));
        const _bar = $("#time_bar");
        _bar.empty();
        const _class = this.ftmax.sub(this.ftmin).div(this.ftint).gt(50)
            ? "time_tab1"
            : "time_tab3";
        // 数字はlabelStepMapの間隔ごとに表示し、その他のコマは「.」にする。
        // 従来の全コマ数字直書きはコマ幅 (time_tab1=12px) を超える数字が
        // 隣コマの背景に塗り潰されて読めなかった。コマ幅・当たり判定は
        // 全コマ同一のまま。GSLだけ数字は日数 (24hごとにft/24)。
        const gsl = shadeVariable.startsWith("GSL");
        const labelStep = this.labelStepMap.get(shadeVariable)!;
        // 両端の空コマ (半コマ幅)。端コマの数字がコマ幅を超えても矢印ボタンへ
        // 重ならないよう余白を作る。クリック等の対象外
        // 中身が空だとinline-blockのbaselineが変わり縦にずれるので、
        // 透明な「.」を入れて通常コマと同じ行箱にする
        const pad = (sign: Decimal) =>
            $("<span>")
                .addClass(`${_class}${sign.gt(0) ? "" : "P"} time_pad`)
                .text(".")
                .appendTo(_bar);
        pad(this.ftmin);
        for (let ft = this.ftmin; ft.lte(this.ftmax); ft = ft.add(this.ftint)) {
            const suffix = ft.gt(0) ? "" : "P";
            const labeled = ft.mod(labelStep).eq(0);
            // labeledコマは「.」の代わりに数字を置く。コマ幅を超える数字は
            // time_numのz-indexで隣コマの背景より前面に描き、inline-flexで
            // 左右均等にはみ出すようセンタリングする
            const cell = $("<span>")
                .attr("id", `t${ft}`)
                .addClass(`${_class}${suffix}${labeled ? " time_num" : ""}`)
                .on("click", () => this.click(ft))
                .on("mouseover", () => this.mouseover(ft))
                .appendTo(_bar);
            const text = labeled
                ? gsl
                    ? ft.abs().div(24).toFixed(0)
                    : ft.abs().toString()
                : ".";
            const squeezeDigits = _class === "time_tab1" ? 2 : 3;
            if (labeled && text.length >= squeezeDigits) {
                // 12pxコマの2桁以上・21pxコマの3桁以上の数字は隣の数字と
                // 近すぎるため横方向へ0.7倍に縮める。transformはinline要素に
                // 効かないのでspanで包む
                cell.append(
                    $("<span>").addClass("time_num_squeeze").text(text),
                );
            } else {
                cell.text(text);
            }
        }
        pad(this.ftmax);
        $("<a>")
            .addClass(this.ftmin.gte(0) ? "time_button" : "time_buttonP")
            .text("←")
            .attr({ unselectable: "on" })
            .on("click", () => this.addStep(-1))
            .prependTo(_bar);
        $("<a>")
            .addClass("time_button")
            .text("→")
            .attr({ unselectable: "on" })
            .on("click", () => this.addStep(1))
            .appendTo(_bar);
        if (!this.initFlag) {
            this.set(
                this.params.has("tb_t")
                    ? this.parseParamFt(this.ftmin)
                    : this.defaultTime(shadeVariable),
            );
            this.initFlag = true;
        } else {
            this.set(this.normalize(this.ft));
        }
    }
    saveParams() {
        this.params.set("tb_t", this.ft.toString());
    }
    getParams() {
        return { ft: this.ft };
    }
    getRangeParams() {
        return {
            interval: this.ftint,
            minimum: this.ftmin,
            maximum: this.ftmax,
        };
    }
    // 先読み状況をタブ配色へ反映 (2026-08-02): ready=false のコマへ time_unready を
    // 付ける (CSS で薄色になる)。呼び出し元は Shade (タイル取得状況を知る場所)。
    // 旧形式表示では呼ばれないため従来色のまま
    setTabReadiness(ready: ReadonlyMap<string, boolean>) {
        for (const [ftText, isReady] of ready) {
            $(`[id='t${ftText}']`).toggleClass("time_unready", !isReady);
        }
    }
    // 先読み対象の近傍 FT (範囲内のみ)。進行方向 (direction) 側 1..ahead を先に、
    // 逆側 1..behind を後に返す (既定 前方12・後方6。2026-08-02 発注者指示)。
    // 動画/ZIP の 0.1h 駆動中 (=UI 刻みに乗らない ft) は毎フレーム別 URL を
    // 先読みしてキャッシュを荒らすだけなので何も返さない
    getNeighborFts(ft: Decimal, direction: 1 | -1 = 1, ahead = 12, behind = 6) {
        if (!ft.mod(this.ftint).isZero()) {
            return [];
        }
        const result: Decimal[] = [];
        for (const [sign, count] of [
            [direction, ahead],
            [-direction, behind],
        ] as const) {
            for (let step = 1; step <= count; step++) {
                const value = ft.add(this.ftint.mul(sign * step));
                if (value.gte(this.ftmin) && value.lte(this.ftmax)) {
                    result.push(value);
                }
            }
        }
        return result;
    }
    loadParams() {
        this.init();
        this.set(
            this.params.has("tb_t")
                ? this.normalize(this.parseParamFt(this.ftmin))
                : this.defaultTime(
                      this.shadeSelector.getParams().shadeVariable,
                  ),
        );
    }
    private parseParamFt(fallback: Decimal) {
        try {
            const value = new Decimal(this.params.get("tb_t")!);
            return value.isFinite() ? value : new Decimal(fallback);
        } catch {
            return new Decimal(fallback);
        }
    }
    set(ft: Decimal) {
        const oldDisplayT = this.ft.div(this.ftint).floor().mul(this.ftint);
        if (oldDisplayT.gt(0)) {
            $(`[id='t${oldDisplayT}']`).removeClass("time_tab_s"); // idにドットが含まれているので。
        } else {
            $(`[id='t${oldDisplayT}']`).removeClass("time_tab_sP");
        }
        this.ft = ft;
        const newDisplayT = this.ft.div(this.ftint).floor().mul(this.ftint);
        if (newDisplayT.gt(0)) {
            $(`[id='t${newDisplayT}']`).addClass("time_tab_s");
        } else {
            $(`[id='t${newDisplayT}']`).addClass("time_tab_sP");
        }
        this.updateTitle();
    }
    updateTitle() {
        const { shadeVariable, model } = this.shadeSelector.getParams();
        const { ini } = this.initialTime.getParams();
        const interval = this.ftintMap.get(shadeVariable)!;
        const start = ini.add(this.ft.toNumber() - interval, "hour");
        const end = ini.add(this.ft.toNumber(), "hour");
        const weekday = ["日", "月", "火", "水", "木", "金", "土"];
        const date = `${start.format("DD")}日(${weekday[start.day()]})`;
        const timeRange =
            model === "LFM"
                ? `${date} ${start.format("HH時mm分")}～${end.format("HH時mm分")}`
                : `${date} ${start.format("HH時")}～${end.format("HH時")}`;
        let forecast: string;
        if (model === "MSG") {
            // 黄砂は GSA と同じ時刻レンジ表記+時間リード (PT96〜FT96)。
            // 0.3h 中間コマは小数1桁で示す。
            const hours = this.ft.abs();
            const leadType = this.ft.isNegative() ? "PT" : "FT";
            const digits = hours.mod(1).isZero() ? 0 : 1;
            this.setTitle(timeRange, `${leadType}=${hours.toFixed(digits)}`);
            return;
        }
        if (model === "GSL") {
            const valid = ini.add(this.ft.mul(60).toNumber(), "minute");
            const target = `${valid.format("MM月DD日")}(${weekday[valid.day()]}) ${valid.format("HH時")}`;
            const leadType = this.ft.isNegative() ? "PD" : "FD";
            const leadDays = this.ft.abs().div(24).toFixed(2);
            this.setTitle(target, `${leadType}=${leadDays}`);
            return;
        } else if (model === "QMA") {
            forecast = `PT=${this.ft.neg().toFixed(0).padStart(2, "0")}`;
        } else if (model === "GS5" || model === "GSA") {
            forecast = `FT=${this.ft.toFixed(0).padStart(3, "0")}`;
        } else if (model === "LFM") {
            forecast = `FT=${this.ft.toFixed(1).padStart(4, "0")}`;
        } else {
            forecast = `FT=${this.ft.toFixed(0).padStart(2, "0")}`;
        }
        this.setTitle(timeRange, forecast);
    }
    private setTitle(target: string, lead: string) {
        $("#FT_target").text(target);
        $("#FT_lead").text(lead);
    }
    onInitialTimeChanged() {
        this.init();
    }
    private mouseover(ft: Decimal) {
        if (!$("#hover_onoff").prop("checked")) {
            this.set(ft);
            this.changeCallback();
        }
    }
    private click(ft: Decimal) {
        if ($("#hover_onoff").prop("checked")) {
            this.set(ft);
            this.changeCallback();
        }
    }
    private add(dt: Decimal) {
        const ftnew = this.ft.add(dt);
        if (ftnew.gt(this.ftmax) || ftnew.lt(this.ftmin)) {
            return;
        }
        this.set(ftnew);
    }
    private addStep(nt: number) {
        this.add(this.ftint.mul(nt));
        this.changeCallback();
    }
    addSubStep(nt: number) {
        const ftint = this.ftint.lt(1) ? new Decimal(1) : this.ftint;
        this.add(ftint.mul(nt));
    }
    private getMaximum(shadeVariable: ShadeVariable) {
        const baseMaximum = this.ftmaxMap.get(shadeVariable)!;
        const { ini } = this.initialTime.getParams();
        const initial = ini.format("YYYYMMDDHHmmss");
        const hour = ini.hour();
        if (shadeVariable.startsWith("MSM")) {
            if (hour !== 9 && hour !== 21) {
                return 39;
            }
            if (initial >= "20220616090000") {
                return 78;
            }
            if (initial >= "20190305090000") {
                return 51;
            }
            return 39;
        }
        if (shadeVariable.startsWith("LFM")) {
            if (hour % 3 !== 0 || initial < "20240305090000") {
                return 10;
            }
            const oldEnough = dayjs().diff(ini, "hour", true) >= 4;
            const latest18 = this.initialTime.getLatest(`${shadeVariable}18`);
            if (oldEnough || (latest18 && ini.isSame(latest18))) {
                return 18;
            }
            return 10;
        }
        if (shadeVariable.startsWith("GSL")) {
            if (hour !== 9 && hour !== 21) {
                return 132;
            }
            const oldEnough = dayjs().diff(ini, "hour", true) >= 7;
            const latest264 = this.initialTime.getLatest("GSLR264");
            return oldEnough || (latest264 && ini.isSame(latest264))
                ? 264
                : 132;
        }
        return baseMaximum;
    }
    private getMinimum(shadeVariable: ShadeVariable) {
        const baseMinimum = this.ftminMap.get(shadeVariable)!;
        if (!shadeVariable.startsWith("GSL") && shadeVariable !== "MSGL") {
            return baseMinimum;
        }
        const { ini } = this.initialTime.getParams();
        const isMsg = shadeVariable === "MSGL";
        const analysisStart = dayjs(
            isMsg ? MSG_ANALYSIS_START : GSL_ANALYSIS_START,
        );
        const availableHours = analysisStart.diff(ini, "minute") / 60;
        const interval = isMsg ? 3 : 6;
        const firstStep = Math.ceil(availableHours / interval) * interval;
        return Math.max(baseMinimum, Math.min(0, firstStep));
    }
    private defaultTime(shadeVariable: ShadeVariable) {
        return shadeVariable.startsWith("GSL") || shadeVariable === "MSGL"
            ? new Decimal(0)
            : new Decimal(this.ftmin);
    }
    private normalize(ft: Decimal) {
        const clamped = ft.gt(this.ftmax)
            ? this.ftmax
            : ft.lt(this.ftmin)
              ? this.ftmin
              : ft;
        return clamped.div(this.ftint).floor().mul(this.ftint);
    }
    onChange(callback: () => void) {
        this.changeCallback = callback;
    }
}
